import { configure, defineRule } from "vee-validate";
import { getTranslations } from "@shared/Services/translations";
import { getStringValue } from "@shared/Services/form-value-service";
import { parseMeasurement } from "@shared/Services/measurement-service";
import { localize } from "@vee-validate/i18n";

const NUMERIC_REGEX = /^[0-9]{1,2}([,.][0-9]{1,4})?$/;
const EMAIL_REGEX = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const MAX_BOAT_LENGTH_METERS = 30;
const MAX_BOAT_WIDTH_METERS = 7;

const getTranslation = (key: string, fallback: string): string => {
  return getTranslations()[key] ?? fallback;
};

defineRule("required", (value: unknown) => {
  if (typeof value === "number") {
    value = value.toString();
  }

  if (!value || !String(value).length) {
    return getTranslation("bookings.validation.required", "This field is required.");
  }

  return true;
});

defineRule("email", (value: unknown) => {
  if (!value || !String(value).length) {
    return true;
  }

  if (!EMAIL_REGEX.test(String(value).toLowerCase())) {
    return getTranslation("bookings.validation.email", "Enter a valid email address.");
  }

  return true;
});

defineRule("min", (value: unknown, [minLength]: unknown[]) => {
  if (!value || !String(value).length) {
    return true;
  }

  if (String(value).length < Number(minLength)) {
    return getTranslation("bookings.validation.passwordlength", "Minimum length not met.");
  }

  return true;
});

defineRule("numeric", (value: unknown) => {
  if (!value || !String(value).length) {
    return true;
  }

  if (!NUMERIC_REGEX.test(String(value).trim())) {
    return getTranslation("bookings.validation.numeric", "Only numbers are allowed.");
  }

  return true;
});

defineRule("dropdown-required", (value: unknown) => {
  const stringValue = getStringValue(value);
  if (!stringValue.length) {
    return getTranslations()["bookings.validation.dropdown-required"];
  }

  return true;
});

defineRule("boat-numeric", (value: unknown) => {
  const stringValue = getStringValue(value);
  if (!stringValue.length) {
    return true;
  }

  if (!NUMERIC_REGEX.test(stringValue.trim())) {
    return getTranslation("bookings.validation.numeric", "Only numbers are allowed.");
  }

  return true;
});

defineRule("boat-length", (value: unknown) => {
  const stringValue = getStringValue(value);
  if (!stringValue.length) {
    return true;
  }

  const parsedValue = parseMeasurement(stringValue);
  if (parsedValue !== null && (parsedValue > MAX_BOAT_LENGTH_METERS || parsedValue <= 0)) {
    return getTranslation("bookings.boatdetails.validation.boatsize", "Enter a valid boat size in meters.");
  }

  return true;
});

defineRule("boat-width", (value: unknown) => {
  const stringValue = getStringValue(value);
  if (!stringValue.length) {
    return true;
  }

  const parsedValue = parseMeasurement(stringValue);
  if (parsedValue !== null && (parsedValue > MAX_BOAT_WIDTH_METERS || parsedValue <= 0)) {
    return getTranslation("bookings.boatdetails.validation.boatsize", "Enter a valid boat size in meters.");
  }

  return true;
});

defineRule("insurance-company", (value: unknown, params: unknown[] | Record<string, unknown> = []) => {
  if (!Array.isArray(params) && params.isBusinessCustomer === true) {
    return true;
  }

  const stringValue = getStringValue(value);
  if (!stringValue.length) {
    return getTranslation("bookings.validation.required", "This field is required.");
  }

  return true;
});

defineRule("accepted", (value: unknown) => {
  if (value !== true) {
    return getTranslation("bookings.validation.acceptterms", "You need to accept the terms and conditions.");
  }

  return true;
});

configure({
  generateMessage: localize('sv', {
    messages: {
      required: 'Måste vara ifyllt',
      min: 'Minst 0:{length} tecken',
      numeric: 'Får endast innehålla siffror',
      email: 'Fyll i en korrekt e-postadress',
    },
  }),
});
